import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Check, MessageCircle, Mail, Menu, X,
  Globe, Target, Shield, RefreshCw, Clock, Users, Phone,
  Zap, Heart, TrendingUp, Award, CalendarCheck, Wallet,
  ChevronRight, ChevronUp, Star
} from "lucide-react";
import CookieBanner from "@/components/CookieBanner";
import LegalModal from "@/components/LegalModal";

const WA = "34641769410";
const W3F = "196ad31e-f8ef-46a6-b227-bf99aedb9e55";

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.55, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionLabel = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-medium mb-4 block">
    {children}
  </span>
);

/* ═══════════════ NAVBAR ═══════════════ */
const navLinks = [
  { label: "Servicios", href: "#servicios" },
  { label: "Cómo trabajamos", href: "#proceso" },
  { label: "Resultados", href: "#resultados" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Contacto", href: "#contacto" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-2xl border-b border-white/[0.06]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 h-16 flex items-center justify-between">
        <a href="#" className="text-lg tracking-tight text-white font-semibold" data-testid="logo">
          Julián<span className="text-[#D4AF37]">Digital</span>Studio
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <a key={l.label} href={l.href} className="text-[13px] text-white/50 hover:text-white transition-colors" data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>
              {l.label}
            </a>
          ))}
          <a href="#contacto" className="btn-primary px-5 py-2 rounded-full text-[13px]" data-testid="nav-cta">
            Quiero más clientes
          </a>
        </nav>
        <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-1.5" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-[#050505]/95 backdrop-blur-2xl border-b border-white/[0.06] px-5 pb-5">
            {navLinks.map((l) => (
              <a key={l.label} href={l.href} className="block py-2.5 text-white/60 hover:text-white transition-colors" onClick={() => setOpen(false)}>
                {l.label}
              </a>
            ))}
            <a href="#contacto" className="btn-primary block text-center px-5 py-3 rounded-full text-sm mt-3" onClick={() => setOpen(false)}>
              Quiero más clientes
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ═══════════════ HERO ═══════════════ */
const trustBar = [
  { icon: TrendingUp, t: "Más clientes desde tu web" },
  { icon: Zap, t: "Lista en días, no meses" },
  { icon: Users, t: "Sin tecnicismos ni líos" },
  { icon: Phone, t: "Soporte directo conmigo" },
];

const Hero = () => (
  <section data-testid="hero-section" className="min-h-screen flex items-center relative overflow-hidden" style={{ backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.93), rgba(5,5,5,0.98)), url('https://images.unsplash.com/photo-1647101734210-ff5d79813069?w=1920&q=60')`, backgroundSize: "cover", backgroundPosition: "center" }}>
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
    <div className="max-w-7xl mx-auto px-5 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24 relative z-10 w-full">
      <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85 }} className="max-w-3xl">
        <SectionLabel>Julián Digital Studio</SectionLabel>
        <h1 className="text-[2.2rem] sm:text-[2.8rem] md:text-[3.4rem] lg:text-[4rem] font-bold tracking-tight leading-[1.08] mb-7">
          Webs para negocios locales que quieren{" "}
          <span className="text-gradient-gold">más clientes</span>
        </h1>
        <p className="text-[15px] md:text-base text-white/45 leading-relaxed mb-10 max-w-xl">
          Diseñamos, desarrollamos y optimizamos webs para negocios locales que quieren verse profesionales y convertir visitas en mensajes, llamadas y reservas.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mb-12">
          <a href="#contacto" className="btn-primary px-7 py-3.5 rounded-full text-[15px] inline-flex items-center justify-center gap-2 font-semibold" data-testid="hero-cta">
            Quiero más clientes <ArrowRight size={17} />
          </a>
          <a href="#portfolio" className="btn-secondary px-7 py-3.5 rounded-full text-[15px] text-center font-medium" data-testid="hero-portfolio-link">
            Ver ejemplos reales
          </a>
        </div>
        <div className="flex flex-wrap gap-x-7 gap-y-2.5">
          {trustBar.map((i) => (
            <div key={i.t} className="flex items-center gap-2" data-testid={`trust-item`}>
              <i.icon size={14} className="text-[#D4AF37]" />
              <span className="text-[12px] text-white/40 tracking-wide">{i.t}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════ SERVICES ═══════════════ */
const services = [
  { icon: Globe, title: "Web Corporativa Premium", desc: "Tu negocio se ve profesional y genera confianza desde el primer segundo." },
  { icon: Target, title: "Landing Page de Captación", desc: "Convierte visitas en mensajes, llamadas y clientes con una página enfocada a una sola acción." },
  { icon: Shield, title: "Mantenimiento Web", desc: "Tu web siempre rápida, segura y funcionando sin que tengas que preocuparte por nada." },
  { icon: RefreshCw, title: "Rediseño Web", desc: "Transformamos una web que no vende en una herramienta que sí transmite nivel y genera contactos." },
];

const Services = () => (
  <section id="servicios" data-testid="services-section" className="section-spacing">
    <div className="container-width">
      <FadeIn>
        <SectionLabel>Servicios</SectionLabel>
        <h2 className="section-title">Soluciones pensadas para vender más</h2>
      </FadeIn>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
        {services.map((s, i) => (
          <FadeIn key={s.title} delay={i * 0.07}>
            <div className="glass-card card-hover rounded-2xl p-7 group h-full" data-testid={`service-card-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
                <s.icon size={19} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-[17px] font-semibold mb-2.5 text-white">{s.title}</h3>
              <p className="text-white/40 text-[14px] leading-relaxed">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════ WHY US ═══════════════ */
const reasons = [
  { icon: MessageCircle, t: "Respuesta por WhatsApp en menos de 24h" },
  { icon: Clock, t: "Web lista en tiempos reales, sin marearte meses" },
  { icon: Zap, t: "Nos ocupamos de lo técnico para que no pierdas tiempo" },
  { icon: Heart, t: "Diseño pensado para generar confianza y contactos" },
  { icon: RefreshCw, t: "Cambios rápidos y trato cercano" },
  { icon: Users, t: "Hablamos claro, sin tecnicismos" },
];

const WhyUs = () => (
  <section data-testid="why-us-section" className="section-spacing bg-[#080808]">
    <div className="container-width">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <FadeIn>
          <SectionLabel>Por qué nos eligen</SectionLabel>
          <h2 className="section-title mb-5">Por qué los negocios nos eligen</h2>
          <p className="text-white/40 text-[15px] leading-relaxed mb-6">
            No vendemos webs bonitas sin más. Creamos herramientas que ayudan a tu negocio a verse mejor, transmitir confianza y conseguir clientes.
          </p>
          <p className="text-[#D4AF37] text-sm font-medium italic">
            "No vendemos webs. Creamos herramientas que te traen clientes."
          </p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="space-y-3">
            {reasons.map((r, i) => (
              <div key={i} className="flex items-start gap-3.5 p-4 rounded-xl border border-white/[0.05] bg-white/[0.01] hover:border-[#D4AF37]/20 transition-colors" data-testid={`reason-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <r.icon size={15} className="text-[#D4AF37]" />
                </div>
                <span className="text-white/60 text-[14px] leading-relaxed">{r.t}</span>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════ PROCESS ═══════════════ */
const steps = [
  { n: "01", title: "Descubrimos tu negocio", desc: "Entendemos qué vendes, qué te diferencia y cómo atraer más clientes." },
  { n: "02", title: "Diseñamos la estrategia", desc: "Estructura, mensaje y enfoque para que tu web convierta, no solo se vea bien." },
  { n: "03", title: "Creamos tu web", desc: "Diseño premium, versión móvil cuidada y base pensada para generar contactos desde el día 1." },
  { n: "04", title: "Lanzamos y mejoramos", desc: "Publicamos, revisamos contigo y dejamos la web lista para funcionar cada vez mejor." },
];

const Process = () => (
  <section id="proceso" data-testid="process-section" className="section-spacing">
    <div className="container-width">
      <FadeIn className="max-w-2xl mb-14">
        <SectionLabel>Cómo trabajamos</SectionLabel>
        <h2 className="section-title mb-4">Un proceso simple y eficaz</h2>
        <p className="text-white/40 text-[15px] leading-relaxed">Sin reuniones interminables ni sorpresas. Te lo ponemos fácil de principio a fin.</p>
      </FadeIn>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {steps.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.08}>
            <div className="relative group" data-testid={`step-${i}`}>
              <span className="text-[4.5rem] md:text-[5.5rem] font-bold text-white/[0.025] absolute -top-4 -left-1 leading-none group-hover:text-[#D4AF37]/[0.05] transition-colors duration-500">{s.n}</span>
              <div className="pt-14 relative z-10">
                <h3 className="text-[17px] font-semibold mb-2 text-white">{s.title}</h3>
                <p className="text-white/40 text-[14px] leading-relaxed">{s.desc}</p>
              </div>
              {i < 3 && <ChevronRight className="hidden lg:block absolute top-1/2 -right-2.5 text-white/[0.08]" size={18} />}
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════ RESULTS ═══════════════ */
const results = [
  { icon: MessageCircle, title: "Más mensajes y consultas", desc: "Desde tu web, cada semana." },
  { icon: CalendarCheck, title: "Más reservas o llamadas", desc: "Diseñada para generar acción real." },
  { icon: Heart, title: "Imagen profesional", desc: "Que genera confianza al instante." },
  { icon: Award, title: "Diferencia clara", desc: "Frente a tu competencia directa." },
];

const Results = () => (
  <section id="resultados" data-testid="results-section" className="section-spacing bg-[#080808]">
    <div className="container-width">
      <FadeIn className="text-center mb-14">
        <SectionLabel>Resultados</SectionLabel>
        <h2 className="section-title">Lo que cambia cuando tu web transmite nivel</h2>
      </FadeIn>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {results.map((r, i) => (
          <FadeIn key={r.title} delay={i * 0.07}>
            <div className="glass-card card-hover rounded-2xl p-7 text-center" data-testid={`result-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <r.icon size={20} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-[15px] font-semibold mb-1.5 text-white">{r.title}</h3>
              <p className="text-white/35 text-[13px] leading-relaxed">{r.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════ HUMAN BLOCK ═══════════════ */
const humanBullets = [
  "Hablas directamente conmigo",
  "Entiendo tu negocio antes de diseñar",
  "Te explico todo sin tecnicismos",
  "Enfoque real en resultados",
];

const HumanBlock = () => (
  <section data-testid="human-section" className="section-spacing">
    <div className="container-width">
      <div className="max-w-3xl mx-auto text-center">
        <FadeIn>
          <SectionLabel>Trato directo</SectionLabel>
          <h2 className="section-title mb-6">Trabajo contigo de forma directa</h2>
          <p className="text-white/40 text-[15px] leading-relaxed mb-10 max-w-xl mx-auto">
            Soy Julián. Creo webs para negocios locales que quieren verse profesionales y conseguir más clientes. Sin intermediarios, sin complicaciones y con un trato claro de principio a fin.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-lg mx-auto">
            {humanBullets.map((b, i) => (
              <div key={i} className="flex items-center gap-2.5 justify-center sm:justify-start" data-testid={`human-bullet-${i}`}>
                <Check size={15} className="text-[#D4AF37] shrink-0" />
                <span className="text-white/55 text-[14px]">{b}</span>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════ PORTFOLIO ═══════════════ */
const portfolio = [
  { cat: "PELUQUERÍA / BARBERÍA", title: "Web pensada para conseguir más citas", desc: "Diseño enfocado en generar citas desde móvil y transmitir una imagen profesional desde el primer vistazo.", img: "https://customer-assets.emergentagent.com/job_quick-audit-2/artifacts/pqeac6ja_web-barberia-clientes-reservas-mockup.jpg.png", alt: "web para barbería diseñada para conseguir más clientes y reservas" },
  { cat: "CLÍNICA / SALUD", title: "Web diseñada para generar citas", desc: "Estructura clara, imagen limpia y enfoque en transmitir seguridad y confianza para convertir visitas en pacientes.", img: "https://customer-assets.emergentagent.com/job_quick-audit-2/artifacts/ctlqhgbw_web-clinica-estetica-generar-citas.jpg.png", alt: "web para clínica estética diseñada para generar citas" },
  { cat: "RESTAURANTE", title: "Web enfocada en reservas reales", desc: "Diseño visualmente potente para destacar, enseñar tu negocio y facilitar que te reserven sin depender de apps.", img: "https://customer-assets.emergentagent.com/job_quick-audit-2/artifacts/ctn2kxpq_web-restaurante-mas-reservas-online.jpg.png", alt: "web para restaurante diseñada para conseguir más reservas online" },
  { cat: "ESTÉTICA / BELLEZA", title: "Web premium para captar interés", desc: "Imagen cuidada, mensaje claro y estructura pensada para generar consultas y reservas de forma constante.", img: "https://customer-assets.emergentagent.com/job_quick-audit-2/artifacts/d6kl71oj_web-centro-estetica-conseguir-clientes.jpg.png", alt: "web para centro de estética diseñada para captar clientes" },
];

const Portfolio = () => (
  <section id="portfolio" data-testid="portfolio-section" className="section-spacing bg-[#080808]">
    <div className="container-width">
      <FadeIn className="mb-14">
        <SectionLabel>Portfolio</SectionLabel>
        <h2 className="section-title mb-4">Así podría verse tu negocio online</h2>
        <p className="text-white/40 text-[15px] max-w-xl">Ejemplos reales del tipo de webs que diseñamos para atraer más negocio, generar confianza y conseguir contactos desde el primer día.</p>
      </FadeIn>
      <div className="grid md:grid-cols-2 gap-5">
        {portfolio.map((p, i) => (
          <FadeIn key={p.title} delay={i * 0.08}>
            <a href="#contacto" className="group relative overflow-hidden rounded-2xl border border-white/[0.05] block cursor-pointer bg-[#0a0a0a]" data-testid={`portfolio-item-${i}`}>
              <div className="aspect-[4/3] overflow-hidden flex items-center justify-center">
                <img src={p.img} alt={p.alt} className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 group-hover:from-black/95 group-hover:via-black/50 transition-all duration-500 flex items-end p-6 md:p-7">
                <div className="transform transition-transform duration-500 group-hover:translate-y-[-4px]">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1.5 block">{p.cat}</span>
                  <h3 className="text-base md:text-lg font-semibold text-white mb-1.5">{p.title}</h3>
                  <p className="text-white/45 text-[13px] leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 max-h-0 group-hover:max-h-20 overflow-hidden">{p.desc}</p>
                </div>
              </div>
            </a>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════ PRICING ═══════════════ */
const plans = [
  {
    name: "Presencia Profesional",
    tag: "Para empezar bien",
    price: "Desde 297€",
    desc: "Ideal para negocios que quieren verse serios y tener una web clara, rápida y profesional.",
    features: ["Web profesional de presentación", "Diseño responsive", "Textos básicos optimizados", "Botón de WhatsApp visible"],
    cta: "Quiero esta opción",
    featured: false,
  },
  {
    name: "Web para Conseguir Clientes",
    tag: "La más elegida",
    price: "Desde 497€",
    desc: "La mejor opción para negocios que quieren una web pensada para captar mensajes, llamadas y reservas.",
    features: ["Web completa de 5 a 7 secciones", "Copy orientado a conversión", "Integración Google Business", "Integración WhatsApp", "Optimización SEO local básica"],
    cta: "Quiero más clientes",
    featured: true,
  },
  {
    name: "Sistema de Captación",
    tag: "Más completo",
    price: "Desde 897€",
    desc: "Para negocios que quieren una presencia fuerte, optimizada y con herramientas reales para captar oportunidades.",
    features: ["Todo lo del plan anterior", "Google Business optimizado", "WhatsApp Business configurado", "Landing de apoyo o captación", "Asesoría estratégica inicial"],
    cta: "Quiero esta solución",
    featured: false,
  },
];

const Pricing = () => (
  <section id="planes" data-testid="pricing-section" className="section-spacing">
    <div className="container-width">
      <FadeIn className="text-center mb-14">
        <SectionLabel>Planes</SectionLabel>
        <h2 className="section-title mb-4">Elige la opción que mejor encaja con tu negocio</h2>
        <p className="text-white/40 text-[15px] max-w-lg mx-auto">No vendemos tecnología. Vendemos una presencia online más profesional y una web pensada para traer clientes.</p>
      </FadeIn>
      <div className="grid md:grid-cols-3 gap-5 items-start">
        {plans.map((p, i) => (
          <FadeIn key={p.name} delay={i * 0.08}>
            <div className={`rounded-2xl p-7 md:p-8 h-full flex flex-col ${
              p.featured
                ? "border-2 border-[#D4AF37]/40 bg-[#D4AF37]/[0.04] relative"
                : "glass-card border border-white/[0.06]"
            }`} data-testid={`pricing-card-${i}`}>
              {p.featured && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-[#050505] text-[11px] font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                    {p.tag}
                  </span>
                </div>
              )}
              {!p.featured && <span className="text-[11px] text-white/30 uppercase tracking-wider mb-3">{p.tag}</span>}
              {p.featured && <div className="mt-3" />}
              <h3 className="text-xl font-semibold text-white mb-2">{p.name}</h3>
              <p className="text-2xl md:text-3xl font-bold text-gradient-gold mb-3">{p.price}</p>
              <p className="text-white/40 text-[14px] leading-relaxed mb-6">{p.desc}</p>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={14} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    <span className="text-white/55 text-[13px]">{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#contacto" className={`block text-center py-3 rounded-full text-[14px] font-semibold transition-all ${
                p.featured ? "btn-primary" : "btn-secondary"
              }`} data-testid={`pricing-cta-${i}`}>
                {p.cta}
              </a>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn>
        <p className="text-center text-white/30 text-[13px] mt-8 max-w-md mx-auto">
          Si en 30 días no estás contento, revisamos contigo lo necesario sin coste extra.
        </p>
      </FadeIn>
    </div>
  </section>
);

/* ═══════════════ OFFER ═══════════════ */
const Offer = () => (
  <section data-testid="offer-section" className="section-spacing bg-[#080808]">
    <div className="container-width">
      <div className="max-w-3xl mx-auto text-center">
        <FadeIn>
          <SectionLabel>Oferta</SectionLabel>
          <h2 className="section-title mb-6">Web profesional lista para conseguir clientes</h2>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-10">
            {["Diseño completo + estructura + textos", "Optimizada para móviles", "Pensada para generar contactos", "Soporte incluido"].map((p) => (
              <div key={p} className="flex items-center gap-2">
                <Check size={14} className="text-[#D4AF37]" />
                <span className="text-white/50 text-[14px]">{p}</span>
              </div>
            ))}
          </div>
          <a href="#contacto" className="btn-primary px-8 py-4 rounded-full text-[15px] inline-flex items-center gap-2 font-semibold" data-testid="offer-cta">
            Pide tu propuesta <ArrowRight size={17} />
          </a>
          <p className="text-white/30 text-[13px] mt-4">Sin compromiso. Te mostramos la idea primero.</p>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════ CONTACT QUIZ ═══════════════ */
const q1 = ["Peluquería o barbería", "Clínica o centro médico", "Centro de estética o belleza", "Restaurante o cafetería", "Gimnasio o fitness", "Otro negocio local"];
const q2 = ["Sí, pero no me convence", "Sí, pero está desactualizada", "No, todavía no tengo web"];
const q3 = ["Más reservas o citas", "Más llamadas o mensajes", "Mejor imagen profesional", "Todo lo anterior"];
const q4 = ["Lo antes posible", "Este mes", "En unas semanas", "Solo estoy mirando opciones"];
const q5 = ["Solo web profesional", "Web + Google Business", "Web + Google Business + WhatsApp Business"];

const DiagnosisSection = () => {
  const [step, setStep] = useState(0);
  const [a, setA] = useState({ biz: "", web: "", goal: "", when: "", plan: "", name: "", contact: "", msg: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const total = 5;
  const progress = ((step + 1) / (total + 1)) * 100;
  const s = (k, v) => setA((p) => ({ ...p, [k]: v }));
  const next = () => step < total && setStep((p) => p + 1);
  const prev = () => step > 0 && setStep((p) => p - 1);
  const ok = () => {
    const c = [a.biz, a.web, a.goal, a.when, a.plan];
    return step < 5 ? c[step] !== "" : true;
  };

  const waMsg = () => encodeURIComponent(`Hola, tengo un ${a.biz.toLowerCase()}. ${a.web}. Quiero una web para ${a.goal.toLowerCase()}. Me interesa: ${a.plan.toLowerCase()}.`);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: W3F,
          subject: `Nuevo lead — ${a.biz}`,
          from_name: a.name,
          tipo_negocio: a.biz,
          web_actual: a.web,
          objetivo: a.goal,
          cuando: a.when,
          plan_interes: a.plan,
          nombre: a.name,
          contacto: a.contact,
          mensaje: a.msg || "—",
        }),
      });
      if (r.ok) setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const Card = ({ value, selected, onClick, children }) => (
    <button type="button" onClick={onClick} className={`w-full text-left p-3.5 rounded-xl border transition-all duration-300 ${selected ? "border-[#D4AF37] bg-[#D4AF37]/[0.07]" : "border-white/[0.06] bg-white/[0.01] hover:border-white/[0.12] hover:bg-white/[0.025]"}`} data-testid={`select-card-${value}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[14px] ${selected ? "text-white" : "text-white/60"}`}>{children}</span>
        {selected && <Check size={15} className="text-[#D4AF37]" />}
      </div>
    </button>
  );

  const Q = ({ title, help, opts, field }) => (
    <div>
      <h3 className="text-xl md:text-2xl font-semibold leading-tight mb-1">{title}</h3>
      {help && <p className="text-white/35 text-[13px] mb-5">{help}</p>}
      {!help && <div className="mb-5" />}
      <div className="grid gap-2">{opts.map((o) => <Card key={o} value={o} selected={a[field] === o} onClick={() => s(field, o)}>{o}</Card>)}</div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return <Q title="¿Qué tipo de negocio tienes?" help="Así podremos orientarte mejor." opts={q1} field="biz" />;
      case 1: return <Q title="¿Tienes web actualmente?" help="Para saber desde dónde partimos." opts={q2} field="web" />;
      case 2: return <Q title="¿Qué quieres conseguir con tu web?" help="Tu objetivo principal." opts={q3} field="goal" />;
      case 3: return <Q title="¿Cuándo quieres empezar?" opts={q4} field="when" />;
      case 4: return <Q title="¿Qué opción encaja más contigo?" opts={q5} field="plan" />;
      case 5:
        return (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h3 className="text-xl md:text-2xl font-semibold mb-1.5 leading-tight">Perfecto. Ya tengo una idea mejor de lo que necesitas.</h3>
              <p className="text-white/35 text-[13px] mb-6">Déjame tus datos y te responderé personalmente con una recomendación clara y honesta.</p>
            </div>
            <div className="space-y-2.5">
              <input type="text" value={a.name} onChange={(e) => s("name", e.target.value)} placeholder="Tu nombre" required className="input-field" data-testid="name-input" />
              <input type="text" value={a.contact} onChange={(e) => s("contact", e.target.value)} placeholder="Tu WhatsApp o email" required className="input-field" data-testid="contact-input" />
              <textarea value={a.msg} onChange={(e) => s("msg", e.target.value)} placeholder="Cuéntame algo importante sobre tu negocio" rows={3} className="input-field resize-none" data-testid="notes-textarea" />
            </div>
            <button type="submit" disabled={!a.name || !a.contact || sending} className="w-full btn-primary py-3.5 rounded-full text-[14px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="submit-btn">
              {sending ? "Enviando..." : "Quiero mi recomendación"}
            </button>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-[11px] text-white/30">
              <span>Respondemos en menos de 24h</span>
              <span>Sin compromiso</span>
              <span>Te diremos exactamente qué necesitas</span>
            </div>
            <div className="pt-5 border-t border-white/[0.05]">
              <p className="text-center text-white/35 text-[13px] mb-3">O escríbeme directamente por WhatsApp</p>
              <a href={`https://wa.me/${WA}?text=${waMsg()}`} target="_blank" rel="noopener noreferrer" className="w-full btn-secondary py-3.5 rounded-full text-[14px] font-medium flex items-center justify-center gap-2" data-testid="whatsapp-btn">
                <MessageCircle size={17} /> Abrir WhatsApp
              </a>
            </div>
          </form>
        );
      default: return null;
    }
  };

  if (sent) {
    return (
      <section id="contacto" data-testid="diagnosis-section" className="section-spacing">
        <div className="max-w-xl mx-auto px-5 md:px-12">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-6">
              <Check size={28} className="text-[#D4AF37]" />
            </div>
            <h3 className="text-2xl font-semibold mb-3">Solicitud recibida</h3>
            <p className="text-white/40 text-[15px] mb-7">Revisaré tu caso y te contactaré lo antes posible.</p>
            <a href={`https://wa.me/${WA}?text=${waMsg()}`} target="_blank" rel="noopener noreferrer" className="btn-secondary px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm" data-testid="whatsapp-success-btn">
              <MessageCircle size={17} /> También por WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contacto" data-testid="diagnosis-section" className="section-spacing">
      <div className="max-w-xl mx-auto px-5 md:px-12">
        <FadeIn className="text-center mb-10">
          <SectionLabel>Contacto</SectionLabel>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">Responde unas preguntas y te orientamos</h2>
          <p className="text-white/35 text-[14px] leading-relaxed max-w-md mx-auto">En menos de un minuto entendemos qué necesitas y te diremos la mejor opción para tu negocio.</p>
        </FadeIn>

        <div className="glass-card rounded-2xl p-5 md:p-8">
          <div className="mb-7">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[11px] text-white/35">{step < 5 ? `Pregunta ${step + 1} de ${total}` : "Último paso"}</span>
              <span className="text-[11px] text-[#D4AF37]">{Math.round(progress)}%</span>
            </div>
            <div className="h-[2px] bg-white/[0.05] rounded-full overflow-hidden">
              <motion.div className="h-full progress-bar rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.45, ease: "easeOut" }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }}>
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {step < 5 && (
            <div className="flex justify-between mt-8 pt-4 border-t border-white/[0.05]">
              <button onClick={prev} disabled={step === 0} className="text-white/35 hover:text-white text-[13px] transition-colors disabled:opacity-20 disabled:cursor-not-allowed" data-testid="prev-btn">Anterior</button>
              <button onClick={next} disabled={!ok()} className="btn-primary px-5 py-2 rounded-full text-[13px] disabled:opacity-35 disabled:cursor-not-allowed flex items-center gap-1.5" data-testid="next-btn">
                Continuar <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════ FOOTER ═══════════════ */
const Footer = ({ onOpenLegal }) => (
  <footer data-testid="footer" className="py-16 md:py-24 bg-[#050505] border-t border-white/[0.04]">
    <div className="container-width">
      <div className="grid md:grid-cols-3 gap-10 md:gap-8 mb-14">
        <div>
          <p className="text-lg font-semibold text-white mb-2">Julián<span className="text-[#D4AF37]">Digital</span>Studio</p>
          <p className="text-white/35 text-[14px] leading-relaxed">Diseño web para negocios que quieren crecer.</p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/25 mb-1">Navegación</span>
          {["Servicios", "Portfolio", "Contacto"].map((l) => (
            <a key={l} href={`#${l.toLowerCase()}`} className="text-white/40 hover:text-white text-[13px] transition-colors">{l}</a>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/25 mb-1">Contacto</span>
          <a href={`https://wa.me/${WA}`} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white text-[13px] transition-colors inline-flex items-center gap-1.5" data-testid="footer-whatsapp"><MessageCircle size={13} /> WhatsApp</a>
          <a href="mailto:julianwebsstudio@gmail.com" className="text-white/40 hover:text-white text-[13px] transition-colors inline-flex items-center gap-1.5" data-testid="footer-email"><Mail size={13} /> julianwebsstudio@gmail.com</a>
        </div>
      </div>
      <div className="pt-7 border-t border-white/[0.04] flex flex-col md:flex-row justify-between items-center gap-3">
        <p className="text-white/25 text-[11px]">© {new Date().getFullYear()} Julián Digital Studio</p>
        <div className="flex gap-5 text-[11px]">
          <button onClick={() => onOpenLegal("legal")} className="text-white/25 hover:text-white/50 transition-colors" data-testid="footer-aviso-legal">Aviso Legal</button>
          <button onClick={() => onOpenLegal("privacy")} className="text-white/25 hover:text-white/50 transition-colors" data-testid="footer-privacidad">Privacidad</button>
          <button onClick={() => onOpenLegal("cookies")} className="text-white/25 hover:text-white/50 transition-colors" data-testid="footer-cookies">Cookies</button>
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════ FLOATING ELEMENTS ═══════════════ */
const waDefaultMsg = encodeURIComponent("Hola, acabo de ver tu web y me gustaría saber cómo trabajas y qué opción encajaría mejor para mi negocio.");

const FloatingWhatsApp = () => {
  const [hover, setHover] = useState(false);

  return (
    <div className="fixed bottom-20 right-5 z-40 flex items-center gap-3" data-testid="floating-whatsapp-wrapper">
      {/* Tooltip */}
      <AnimatePresence>
        {hover && (
          <motion.span
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.2 }}
            className="bg-white text-[#050505] text-[13px] font-medium px-3.5 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
          >
            ¿Hablamos por WhatsApp?
          </motion.span>
        )}
      </AnimatePresence>

      <a
        href={`https://wa.me/${WA}?text=${waDefaultMsg}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-14 h-14 bg-[#25D366] hover:bg-[#1EBE5D] rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#25D366]/40"
        data-testid="floating-whatsapp"
        aria-label="Contactar por WhatsApp"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <svg viewBox="0 0 32 32" width="26" height="26" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.004 2.002C8.27 2.002 2.004 8.268 2.004 15.998c0 2.467.644 4.877 1.87 6.996L2 30l7.19-1.887a13.94 13.94 0 006.808 1.779h.006c7.73 0 14-6.266 14-13.996-.004-3.74-1.46-7.254-4.1-9.898a13.9 13.9 0 00-9.9-3.996zm0 25.6a11.58 11.58 0 01-5.906-1.617l-.424-.252-4.394 1.153 1.174-4.29-.276-.44A11.57 11.57 0 014.4 15.998c0-6.4 5.21-11.606 11.61-11.606 3.1 0 6.013 1.21 8.2 3.4a11.52 11.52 0 013.394 8.21c-.004 6.4-5.21 11.6-11.6 11.6zm6.36-8.688c-.348-.174-2.063-1.02-2.383-1.136-.32-.116-.553-.174-.785.174-.232.35-.9 1.136-1.103 1.37-.204.232-.408.26-.756.086-.348-.174-1.47-.542-2.8-1.728-1.035-.922-1.733-2.062-1.937-2.41-.204-.35-.022-.538.153-.712.158-.156.348-.408.522-.612.174-.204.232-.35.348-.582.116-.232.058-.436-.03-.61-.086-.174-.784-1.892-1.074-2.592-.282-.68-.57-.588-.784-.598l-.668-.012a1.28 1.28 0 00-.928.436c-.32.35-1.22 1.194-1.22 2.912 0 1.718 1.248 3.378 1.422 3.612.174.232 2.458 3.752 5.956 5.264.832.36 1.482.574 1.99.736.836.266 1.596.228 2.198.138.67-.1 2.063-.844 2.353-1.66.29-.814.29-1.514.204-1.66-.088-.144-.32-.232-.67-.404z"/>
        </svg>
      </a>
    </div>
  );
};

const ScrollToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  if (!show) return null;
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-20 left-5 z-40 w-10 h-10 bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.08] rounded-full flex items-center justify-center transition-colors"
      data-testid="scroll-to-top"
      aria-label="Volver arriba"
    >
      <ChevronUp size={18} className="text-white/60" />
    </motion.button>
  );
};

/* ═══════════════ APP ═══════════════ */
function App() {
  const [legalModal, setLegalModal] = useState({ open: false, type: null });
  const openLegal = useCallback((type) => setLegalModal({ open: true, type }), []);
  const closeLegal = useCallback(() => setLegalModal({ open: false, type: null }), []);

  useEffect(() => {
    const h = (e) => openLegal(e.detail);
    window.addEventListener("open-legal", h);
    return () => window.removeEventListener("open-legal", h);
  }, [openLegal]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F2EA]">
      <Header />
      <main>
        <Hero />
        <Services />
        <WhyUs />
        <Process />
        <Results />
        <HumanBlock />
        <Portfolio />
        <Pricing />
        <Offer />
        <DiagnosisSection />
      </main>
      <Footer onOpenLegal={openLegal} />
      <FloatingWhatsApp />
      <ScrollToTop />
      <CookieBanner />
      <LegalModal isOpen={legalModal.open} onClose={closeLegal} type={legalModal.type} />
    </div>
  );
}

export default App;
